import { useState } from 'react';
import { Button, Input, toast } from '@ponzinomics/ui';
import type { Quest } from '@ponzinomics/types';
import { useQuests, useCreateQuest, useUpdateQuest, useDeleteQuest } from '../../hooks/use-quests';

// Simplified task types - only 2 options
type TaskType = 'ENGAGEMENT' | 'FOLLOW';

interface Task {
  id: string;
  type: TaskType;
  name: string;
  description: string;
  points: number;
  status: 'DRAFT' | 'ACTIVE' | 'COMPLETED';
  completions: number;
  config: EngagementConfig | FollowConfig;
}

interface EngagementConfig {
  type: 'ENGAGEMENT';
  tweetUrl: string;
}

interface FollowConfig {
  type: 'FOLLOW';
  accounts: string[];
}

// Demo tasks for judge presentation when API returns empty
const DEMO_TASKS: Task[] = [
  {
    id: 'demo-1',
    type: 'ENGAGEMENT',
    name: 'Engage with Launch Tweet',
    description: 'Like, retweet, and comment on our launch announcement',
    points: 100,
    status: 'ACTIVE',
    completions: 2847,
    config: { type: 'ENGAGEMENT', tweetUrl: 'https://x.com/ponzinomics_io/status/1234567890' },
  },
  {
    id: 'demo-2',
    type: 'FOLLOW',
    name: 'Follow the Team',
    description: 'Follow our founding team on X for exclusive alpha',
    points: 50,
    status: 'ACTIVE',
    completions: 4192,
    config: { type: 'FOLLOW', accounts: ['ponzinomics_io', 'founder_xyz', 'devlead_abc'] },
  },
  {
    id: 'demo-3',
    type: 'ENGAGEMENT',
    name: 'Tokenomics Thread',
    description: 'Engage with our detailed tokenomics breakdown thread',
    points: 200,
    status: 'DRAFT',
    completions: 0,
    config: { type: 'ENGAGEMENT', tweetUrl: 'https://x.com/ponzinomics_io/status/9876543210' },
  },
];

function questToTask(quest: Quest): Task {
  const isFollowType = quest.type === 'twitter_follow';
  return {
    id: quest.id,
    type: isFollowType ? 'FOLLOW' : 'ENGAGEMENT',
    name: quest.title,
    description: quest.description,
    points: quest.points,
    status:
      quest.status === 'active' ? 'ACTIVE' : quest.status === 'completed' ? 'COMPLETED' : 'DRAFT',
    completions: quest.completionCount,
    config: isFollowType
      ? { type: 'FOLLOW', accounts: quest.requirements.map((r) => r.value) }
      : { type: 'ENGAGEMENT', tweetUrl: quest.requirements[0]?.value ?? '' },
  };
}

export function QuestsPage(): React.ReactElement {
  const { data: quests, isLoading } = useQuests();
  const createQuest = useCreateQuest();
  const updateQuest = useUpdateQuest();
  const deleteQuest = useDeleteQuest();
  const [showCreator, setShowCreator] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [raidingTask, setRaidingTask] = useState<Task | null>(null);

  const apiTasks: Task[] = (quests ?? []).map(questToTask);
  const tasks: Task[] = apiTasks.length > 0 ? apiTasks : DEMO_TASKS;

  const handleCreateTask = async (task: Task): Promise<void> => {
    const requirements =
      task.type === 'ENGAGEMENT'
        ? [{ type: 'twitter_like', value: (task.config as EngagementConfig).tweetUrl }]
        : (task.config as FollowConfig).accounts.map((a) => ({ type: 'twitter_follow', value: a }));

    try {
      if (editingTask) {
        await updateQuest.mutateAsync({
          id: editingTask.id,
          data: {
            title: task.name,
            description: task.description,
            points: task.points,
            requirements,
          },
        });
        toast({ title: 'Task updated' });
      } else {
        await createQuest.mutateAsync({
          title: task.name,
          description: task.description,
          type: task.type === 'FOLLOW' ? 'twitter_follow' : 'twitter_like',
          points: task.points,
          requirements,
        });
        toast({ title: 'Task created' });
      }
      setShowCreator(false);
      setEditingTask(null);
    } catch {
      toast({ title: 'Failed to save task', variant: 'destructive' });
    }
  };

  const handleDeleteTask = async (taskId: string): Promise<void> => {
    try {
      await deleteQuest.mutateAsync(taskId);
      toast({ title: 'Task deleted' });
    } catch {
      toast({ title: 'Failed to delete task', variant: 'destructive' });
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-white">Tasks</h1>
            <p className="mt-1 text-gray-500">Create engagement tasks for your community</p>
          </div>
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-6">
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 animate-pulse rounded-xl bg-white/[0.05]" />
                <div className="flex-1 space-y-2">
                  <div className="h-5 w-48 animate-pulse rounded bg-white/[0.05]" />
                  <div className="h-4 w-72 animate-pulse rounded bg-white/[0.05]" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white">Tasks</h1>
          <p className="mt-1 text-gray-500">Create engagement tasks for your community</p>
        </div>
        <Button onClick={() => setShowCreator(true)}>
          <PlusIcon className="mr-2 h-4 w-4" />
          New Task
        </Button>
      </div>

      {/* Task List */}
      {tasks.length === 0 ? (
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-sm">
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white/[0.05]">
              <TargetIcon className="h-7 w-7 text-gray-500" />
            </div>
            <h3 className="mt-4 text-lg font-medium text-white">No tasks yet</h3>
            <p className="mt-1 text-sm text-gray-400">
              Create your first task to engage your community
            </p>
            <Button className="mt-6" onClick={() => setShowCreator(true)}>
              Create Task
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {tasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              onClick={() => setRaidingTask(task)}
              onEdit={(e) => {
                e.stopPropagation();
                setEditingTask(task);
                setShowCreator(true);
              }}
              onDelete={(e) => {
                e.stopPropagation();
                handleDeleteTask(task.id);
              }}
            />
          ))}
        </div>
      )}

      {/* Task Creator Modal */}
      {showCreator && (
        <TaskCreator
          task={editingTask}
          onSave={handleCreateTask}
          onClose={() => {
            setShowCreator(false);
            setEditingTask(null);
          }}
        />
      )}

      {/* Raiding Modal */}
      {raidingTask && <QuestRaidingModal task={raidingTask} onClose={() => setRaidingTask(null)} />}
    </div>
  );
}

function TaskCard({
  task,
  onClick,
  onEdit,
  onDelete,
}: {
  task: Task;
  onClick: () => void;
  onEdit: (e: React.MouseEvent) => void;
  onDelete: (e: React.MouseEvent) => void;
}): React.ReactElement {
  const isEngagement = task.type === 'ENGAGEMENT';

  return (
    <div
      onClick={onClick}
      className="notch-border-lg notch-border-white/8 notch-bg-white/2 hover:notch-border-white/12 hover:notch-bg-white/5 cursor-pointer backdrop-blur-sm transition-all duration-200"
    >
      <div className="p-6">
        <div className="flex items-start gap-4">
          {/* Icon */}
          <div
            className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${
              isEngagement ? 'bg-orange-500/10' : 'bg-blue-500/10'
            }`}
          >
            {isEngagement ? (
              <FlameIcon className="h-6 w-6 text-orange-400" />
            ) : (
              <UsersIcon className="h-6 w-6 text-blue-400" />
            )}
          </div>

          {/* Content */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-semibold text-white">{task.name}</h3>
              <StatusBadge status={task.status} />
            </div>
            <p className="mt-1 text-sm text-gray-400">{task.description}</p>

            {/* Task Actions */}
            <div className="mt-4 flex flex-wrap gap-2">
              {isEngagement ? (
                <>
                  <ActionBadge icon={<HeartIcon className="h-3.5 w-3.5" />} label="Like" />
                  <ActionBadge icon={<RepeatIcon className="h-3.5 w-3.5" />} label="Retweet" />
                  <ActionBadge icon={<MessageIcon className="h-3.5 w-3.5" />} label="Comment" />
                </>
              ) : (
                (task.config as FollowConfig).accounts.map((account) => (
                  <ActionBadge
                    key={account}
                    icon={<UserIcon className="h-3.5 w-3.5" />}
                    label={`@${account}`}
                  />
                ))
              )}
            </div>
          </div>

          {/* Stats */}
          <div className="flex items-center gap-6">
            <div className="text-center">
              <p className="text-brand-400 text-2xl font-bold">{task.points}</p>
              <p className="text-xs text-gray-500">points</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-white">{task.completions.toLocaleString()}</p>
              <p className="text-xs text-gray-500">completions</p>
            </div>
            <div className="flex gap-1">
              <button
                onClick={onEdit}
                className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-white/[0.05] hover:text-white"
              >
                <PencilIcon className="h-4 w-4" />
              </button>
              <button
                onClick={onDelete}
                className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-red-500/10 hover:text-red-400"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: Task['status'] }): React.ReactElement {
  const styles = {
    ACTIVE: 'bg-green-500/15 text-green-400 border-green-500/20',
    DRAFT: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/20',
    COMPLETED: 'bg-gray-500/15 text-gray-400 border-gray-500/20',
  };

  return (
    <span className={`rounded-md border px-2 py-0.5 text-xs font-medium ${styles[status]}`}>
      {status}
    </span>
  );
}

function ActionBadge({
  icon,
  label,
}: {
  icon: React.ReactNode;
  label: string;
}): React.ReactElement {
  return (
    <span className="flex items-center gap-1.5 rounded-lg border border-white/[0.1] bg-white/[0.03] px-2.5 py-1.5 text-xs font-medium text-gray-300">
      {icon}
      {label}
    </span>
  );
}

function TaskCreator({
  task,
  onSave,
  onClose,
}: {
  task: Task | null;
  onSave: (task: Task) => void;
  onClose: () => void;
}): React.ReactElement {
  const [selectedType, setSelectedType] = useState<TaskType | null>(task?.type || null);
  const [name, setName] = useState(task?.name || '');
  const [description, setDescription] = useState(task?.description || '');
  const [points, setPoints] = useState(task?.points || 100);

  const [tweetUrl, setTweetUrl] = useState(
    task?.type === 'ENGAGEMENT' ? (task.config as EngagementConfig).tweetUrl : ''
  );

  const [accountsInput, setAccountsInput] = useState(
    task?.type === 'FOLLOW' ? (task.config as FollowConfig).accounts.join(', ') : ''
  );

  const handleSubmit = (): void => {
    if (!selectedType) return;

    const config: EngagementConfig | FollowConfig =
      selectedType === 'ENGAGEMENT'
        ? { type: 'ENGAGEMENT', tweetUrl }
        : {
            type: 'FOLLOW',
            accounts: accountsInput
              .split(',')
              .map((a) => a.trim().replace('@', ''))
              .filter(Boolean),
          };

    onSave({
      id: task?.id || '',
      type: selectedType,
      name,
      description,
      points,
      status: 'DRAFT',
      completions: task?.completions || 0,
      config,
    });
  };

  const isValid =
    selectedType &&
    name.trim() &&
    (selectedType === 'ENGAGEMENT'
      ? tweetUrl.includes('x.com') || tweetUrl.includes('twitter.com')
      : accountsInput.trim());

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
      <div className="w-full max-w-lg overflow-hidden rounded-2xl border border-white/[0.1] bg-gray-900 shadow-2xl">
        {/* Header */}
        <div className="border-b border-white/[0.06] px-6 py-5">
          <h2 className="text-xl font-semibold text-white">{task ? 'Edit Task' : 'Create Task'}</h2>
          <p className="mt-1 text-sm text-gray-400">Choose a task type for your community</p>
        </div>

        {/* Content */}
        <div className="space-y-6 p-6">
          {/* Task Type Selection */}
          <div>
            <label className="mb-3 block text-sm font-medium text-gray-300">Task Type</label>
            <div className="grid grid-cols-2 gap-3">
              <TaskTypeCard
                icon={<FlameIcon className="h-6 w-6" />}
                title="Engagement"
                description="Like, Retweet & Comment"
                selected={selectedType === 'ENGAGEMENT'}
                color="orange"
                onClick={() => setSelectedType('ENGAGEMENT')}
              />
              <TaskTypeCard
                icon={<UsersIcon className="h-6 w-6" />}
                title="Follow"
                description="Follow account(s)"
                selected={selectedType === 'FOLLOW'}
                color="blue"
                onClick={() => setSelectedType('FOLLOW')}
              />
            </div>
          </div>

          {/* Form Fields */}
          {selectedType && (
            <>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-300">Task Name</label>
                <Input
                  placeholder={
                    selectedType === 'ENGAGEMENT'
                      ? 'e.g. Engage with Launch Tweet'
                      : 'e.g. Follow the Team'
                  }
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-300">Description</label>
                <textarea
                  placeholder="Brief description of the task..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                  className="focus:border-brand-400 focus:ring-brand-400/50 w-full resize-none rounded-lg border border-white/[0.1] bg-white/[0.03] px-3 py-2.5 text-sm text-white transition-colors placeholder:text-gray-500 focus:bg-white/[0.05] focus:outline-none focus:ring-1"
                />
              </div>

              {selectedType === 'ENGAGEMENT' ? (
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-300">Tweet URL</label>
                  <Input
                    placeholder="https://x.com/username/status/1234567890"
                    value={tweetUrl}
                    onChange={(e) => setTweetUrl(e.target.value)}
                  />
                  <p className="mt-2 flex items-center gap-1.5 text-xs text-gray-500">
                    <InfoIcon className="h-3.5 w-3.5" />
                    Users will need to like, retweet, and comment
                  </p>
                </div>
              ) : (
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-300">
                    Account(s) to Follow
                  </label>
                  <Input
                    placeholder="@ponzinomics_io, @founder1, @founder2"
                    value={accountsInput}
                    onChange={(e) => setAccountsInput(e.target.value)}
                  />
                  <p className="mt-2 flex items-center gap-1.5 text-xs text-gray-500">
                    <InfoIcon className="h-3.5 w-3.5" />
                    Separate multiple accounts with commas
                  </p>
                </div>
              )}

              {/* Points */}
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-300">
                  Reward Points
                </label>
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Input
                      type="number"
                      value={points}
                      onChange={(e) => setPoints(parseInt(e.target.value) || 0)}
                      className="w-28 pr-8 text-center"
                      min={1}
                    />
                    <StarIcon className="text-brand-400 pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2" />
                  </div>
                  <div className="flex gap-1.5">
                    {[25, 50, 100, 200].map((preset) => (
                      <button
                        key={preset}
                        onClick={() => setPoints(preset)}
                        className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                          points === preset
                            ? 'bg-brand-400 text-gray-900'
                            : 'bg-white/[0.05] text-gray-400 hover:bg-white/[0.1] hover:text-white'
                        }`}
                      >
                        {preset}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t border-white/[0.06] bg-white/[0.02] px-6 py-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!isValid}>
            {task ? 'Save Changes' : 'Create Task'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function QuestRaidingModal({
  task,
  onClose,
}: {
  task: Task;
  onClose: () => void;
}): React.ReactElement {
  const isEngagement = task.type === 'ENGAGEMENT';
  const [raiding, setRaiding] = useState(false);

  const handleRaid = () => {
    setRaiding(true);
    setTimeout(() => {
      setRaiding(false);
      toast({
        title: 'Raid simulation complete',
        description: 'Bot fleet simulated for this quest',
      });
    }, 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
      <div className="w-full max-w-2xl overflow-hidden rounded-2xl border border-white/[0.1] bg-gray-900 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-white/[0.06] px-6 py-5">
          <div>
            <div className="mb-1 flex items-center gap-2">
              <h2 className="text-xl font-semibold text-white">{task.name}</h2>
              <StatusBadge status={task.status} />
            </div>
            <p className="text-sm text-gray-400">{task.type} Quest</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="grid gap-6 md:grid-cols-2">
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                Requirement
              </h3>
              <div className="mt-2 rounded-xl border border-white/[0.06] bg-white/[0.03] p-4">
                {isEngagement ? (
                  <div className="flex flex-col gap-2">
                    <p className="text-sm font-medium text-white">Tweet Target</p>
                    <a
                      href={(task.config as EngagementConfig).tweetUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="truncate text-xs text-blue-400 hover:underline"
                    >
                      {(task.config as EngagementConfig).tweetUrl}
                    </a>
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    <p className="text-sm font-medium text-white">Accounts to Follow</p>
                    <div className="flex flex-wrap gap-2">
                      {(task.config as FollowConfig).accounts.map((acc) => (
                        <span
                          key={acc}
                          className="rounded-lg bg-blue-500/10 px-2 py-1 text-xs text-blue-400"
                        >
                          @{acc}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Description
                </h3>
                <p className="mt-2 text-sm text-gray-300">{task.description}</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="rounded-xl border border-white/[0.06] bg-white/[0.03] p-4 text-center">
                <p className="text-brand-400 text-3xl font-bold">{task.points}</p>
                <p className="text-xs uppercase tracking-widest text-gray-500">Reward Points</p>
              </div>

              <div className="rounded-xl border border-white/[0.06] bg-white/[0.03] p-4 text-center">
                <p className="text-3xl font-bold text-white">{task.completions.toLocaleString()}</p>
                <p className="text-xs uppercase tracking-widest text-gray-500">Total Completions</p>
              </div>

              <Button
                className="w-full py-6 text-lg"
                onClick={handleRaid}
                disabled={raiding || task.status !== 'ACTIVE'}
              >
                {raiding ? (
                  <span className="flex items-center gap-2">
                    <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Simulating Raid...
                  </span>
                ) : (
                  <>
                    <ZapIcon className="mr-2 h-5 w-5" />
                    Simulate Raid
                  </>
                )}
              </Button>
              {task.status !== 'ACTIVE' && (
                <p className="text-center text-xs text-amber-500">
                  Quest must be ACTIVE to simulate raiding
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ZapIcon({ className }: { className?: string }): React.ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z"
      />
    </svg>
  );
}

function TaskTypeCard({
  icon,
  title,
  description,
  selected,
  color,
  onClick,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  selected: boolean;
  color: 'orange' | 'blue';
  onClick: () => void;
}): React.ReactElement {
  const colors = {
    orange: {
      bg: selected ? 'bg-orange-500/10' : 'bg-white/[0.02]',
      border: selected ? 'border-orange-500/50' : 'border-white/[0.08]',
      icon: 'text-orange-400',
    },
    blue: {
      bg: selected ? 'bg-blue-500/10' : 'bg-white/[0.02]',
      border: selected ? 'border-blue-500/50' : 'border-white/[0.08]',
      icon: 'text-blue-400',
    },
  };

  const c = colors[color];

  return (
    <button
      onClick={onClick}
      className={`flex flex-col items-center rounded-xl border-2 p-5 text-center transition-all hover:bg-white/[0.04] ${c.bg} ${c.border}`}
    >
      <div className={c.icon}>{icon}</div>
      <p className="mt-3 font-semibold text-white">{title}</p>
      <p className="mt-0.5 text-xs text-gray-500">{description}</p>
    </button>
  );
}

// Icons
function PlusIcon({ className }: { className?: string }): React.ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
    </svg>
  );
}

function TargetIcon({ className }: { className?: string }): React.ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 8.25v-1.5m0 1.5c-1.355 0-2.697.056-4.024.166C6.845 8.51 6 9.473 6 10.608v2.513m6-4.87c1.355 0 2.697.055 4.024.165C17.155 8.51 18 9.473 18 10.608v2.513m-3-4.87v-1.5m-6 1.5v-1.5m12 9.75l-1.5.75a3.354 3.354 0 01-3 0 3.354 3.354 0 00-3 0 3.354 3.354 0 01-3 0 3.354 3.354 0 00-3 0 3.354 3.354 0 01-3 0L3 16.5m15-3.38a48.474 48.474 0 00-6-.37c-2.032 0-4.034.125-6 .37m12 0c.39.049.777.102 1.163.16 1.07.16 1.837 1.094 1.837 2.175v5.17c0 .62-.504 1.124-1.125 1.124H4.125A1.125 1.125 0 013 20.625v-5.17c0-1.08.768-2.014 1.837-2.174A47.78 47.78 0 016 13.12M12 12.75h.008v.008H12v-.008z"
      />
    </svg>
  );
}

function FlameIcon({ className }: { className?: string }): React.ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.362 5.214A8.252 8.252 0 0112 21 8.25 8.25 0 016.038 7.047 8.287 8.287 0 009 9.601a8.983 8.983 0 013.361-6.867 8.21 8.21 0 003 2.48z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 18a3.75 3.75 0 00.495-7.468 5.99 5.99 0 00-1.925 3.547 5.975 5.975 0 01-2.133-1.001A3.75 3.75 0 0012 18z"
      />
    </svg>
  );
}

function UsersIcon({ className }: { className?: string }): React.ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"
      />
    </svg>
  );
}

function HeartIcon({ className }: { className?: string }): React.ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"
      />
    </svg>
  );
}

function RepeatIcon({ className }: { className?: string }): React.ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 12c0-1.232-.046-2.453-.138-3.662a4.006 4.006 0 00-3.7-3.7 48.678 48.678 0 00-7.324 0 4.006 4.006 0 00-3.7 3.7c-.017.22-.032.441-.046.662M19.5 12l3-3m-3 3l-3-3m-12 3c0 1.232.046 2.453.138 3.662a4.006 4.006 0 003.7 3.7 48.656 48.656 0 007.324 0 4.006 4.006 0 003.7-3.7c.017-.22.032-.441.046-.662M4.5 12l3 3m-3-3l-3 3"
      />
    </svg>
  );
}

function MessageIcon({ className }: { className?: string }): React.ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z"
      />
    </svg>
  );
}

function UserIcon({ className }: { className?: string }): React.ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
      />
    </svg>
  );
}

function PencilIcon({ className }: { className?: string }): React.ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125"
      />
    </svg>
  );
}

function TrashIcon({ className }: { className?: string }): React.ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
      />
    </svg>
  );
}

function InfoIcon({ className }: { className?: string }): React.ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
      />
    </svg>
  );
}

function StarIcon({ className }: { className?: string }): React.ReactElement {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  );
}
